import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  MenuAlt4Icon,
  UserGroupIcon,
  EyeIcon,
  EyeOffIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmModal from '../../../../../common/DeleteConfirmModal';
import PermissionGuard from '../../../../../common/PermissionGuard';

export default function Group({ group, onEdit, onStatusChange, onDelete }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: group.id,
    data: {
      type: 'group',
      item: group
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`relative flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg
          border border-gray-200 dark:border-gray-700 shadow-sm cursor-default overflow-visible
          ${isDragging ? 'opacity-50 shadow-lg scale-105' : ''}`}
      >
        <div className="flex items-center gap-3 overflow-visible">
          {/* مقبض السحب */}
          <div className="relative group overflow-visible flex items-center">
            <div
              {...attributes}
              {...listeners}
              className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <MenuAlt4Icon className="w-4 h-4" />
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              اسحب لإعادة الترتيب
            </div>
          </div>

          {/* أيقونة نوع المحتوى */}
          <div className="relative group overflow-visible flex items-center">
            <UserGroupIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              قروب تعليمي
            </div>
          </div>

          <div className="relative group overflow-visible flex items-center">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {group.title}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                قروب تعليمي
              </p>
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              عرض تفاصيل القروب
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-visible">
          {/* زر تعديل */}
          <PermissionGuard permission="edit_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit && onEdit(group.id);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                تعديل القروب
              </div>
            </div>
          </PermissionGuard>

          {/* زر نشر/إخفاء */}
          <div className="relative group overflow-visible flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange && onStatusChange(group.id);
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
            >
              {group.status === 'published' ? (
                <EyeIcon className="w-4 h-4 text-green-500" />
              ) : (
                <EyeOffIcon className="w-4 h-4" />
              )}
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              {group.status === 'published' ? 'إخفاء القروب' : 'نشر القروب'}
            </div>
          </div>

          {/* زر حذف */}
          <PermissionGuard permission="delete_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
                className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                حذف القروب
              </div>
            </div>
          </PermissionGuard>
        </div>
      </div>

      {/* مودال تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          onDelete && onDelete(group.id);
          setShowDeleteModal(false);
        }}
        itemType="القروب التعليمي"
        itemTitle={group.title}
      />
    </>
  );
}
